// A memo reference highlighter: standalone all-caps tokens that ARE a Flow user's
// Kürzel get tinted in THAT person's identity hue (same colour as their avatar).
// NOT an @-mention — purely presentational, no side effects. The directory gate
// (dir.get) stops non-person acronyms (IT/KPI/SAP); the letter-boundary lookarounds
// stop embedded matches (RSI inside VeRSIcherung).

import { identityInk } from "@/lib/colors";

export interface KuerzelPerson {
  id: string;
  name: string | null;
}
/** UPPERCASE Kürzel → the person, for O(1) lookup during render. */
export type KuerzelDir = Map<string, KuerzelPerson>;

export type Segment =
  | { t: "text"; value: string }
  | { t: "k"; value: string; id: string; name: string | null };

const TOKEN = /(?<![A-Za-z])[A-Z]{2,}(?![A-Za-z])/g;

export function splitKuerzel(text: string, dir: KuerzelDir): Segment[] {
  const out: Segment[] = [];
  let last = 0;
  for (const m of text.matchAll(TOKEN)) {
    const token = m[0];
    const person = dir.get(token);
    if (!person) continue;
    const start = m.index ?? 0;
    if (start > last) out.push({ t: "text", value: text.slice(last, start) });
    out.push({ t: "k", value: token, id: person.id, name: person.name });
    last = start + token.length;
  }
  if (last < text.length) out.push({ t: "text", value: text.slice(last) });
  return out;
}

// Minimal hast shapes we touch (avoids a hast-types dependency).
interface HastText {
  type: "text";
  value: string;
}
interface HastElement {
  type: "element";
  tagName: string;
  properties?: Record<string, unknown>;
  children: HastNode[];
}
type HastNode = HastText | HastElement | { type: string; children?: HastNode[] };

const SKIP = new Set(["a", "code", "pre"]);

function spanFor(seg: { value: string; id: string; name: string | null }): HastElement {
  const ink = identityInk(seg.id);
  // Per-person colour via CSS custom props so the theme rule picks light vs dark.
  // react-markdown (hast-util-to-jsx-runtime) parses this style string into an object.
  return {
    type: "element",
    tagName: "span",
    properties: {
      className: ["flow-kuerzel"],
      title: seg.name ?? undefined,
      style: `--k-l:${ink.light};--k-d:${ink.dark}`,
    },
    children: [{ type: "text", value: seg.value }],
  };
}

/**
 * rehype plugin. Runs AFTER sanitize (the spans it adds are trusted, so sanitize
 * never sees them) and skips a/code/pre so mention chips, links and code are left
 * exactly as they are.
 */
export function rehypeKuerzel(dir: KuerzelDir) {
  return () => (tree: HastNode) => {
    const walk = (node: HastNode) => {
      if (!("children" in node) || !node.children) return;
      if (node.type === "element" && SKIP.has((node as HastElement).tagName)) return;
      const next: HastNode[] = [];
      for (const child of node.children) {
        if (child.type === "text") {
          const segs = splitKuerzel((child as HastText).value, dir);
          if (segs.length === 1 && segs[0]?.t === "text") {
            next.push(child);
            continue;
          }
          for (const s of segs) {
            next.push(s.t === "text" ? { type: "text", value: s.value } : spanFor(s));
          }
        } else {
          walk(child);
          next.push(child);
        }
      }
      node.children = next;
    };
    walk(tree);
  };
}
